<script setup>
import {
    onBeforeMount, onErrorCaptured,
} from 'vue';
import {
    useGtm,
} from '@gtm-support/vue-gtm';
import {
    useRoute, useRouter,
} from 'vue-router';
import {
    getCookie,
} from '../utils/cookie';
import {
    enableTrackers,
    COOKIE_SERVER_SIDE_EVENTS,
    COOKIE_GA4_SERVER_SIDE_EVENTS,
    trackServerSideEvents,
    trackGa4ServerSideEvents,
} from '../utils/tracking';
import config from '../config';
import * as logger from '../utils/logger';
import * as notificationsService from '../services/notification';

import AppPopup from './molecules/app/AppPopup';
import AppSnackbar from './molecules/app/AppSnackbar';
import {
    useNotificationsStore,
} from '../stores/notifications-store';
import {
    shouldShowSnackbar, snackbarData,
} from '../utils/snackbar';
import {
    useAuth,
} from '../stores/user-store';
import {
    useHeartbeat,
} from '../composables/heartbeat';
import LayoutLoading from './layouts/LayoutLoading';
import {
    useLoadingStore,
} from '../stores/loading-store';

const TIME_NOTIFICATIONS_USER = 30 * 1000;

const router = useRouter();
const route = useRoute();
const auth = useAuth();

useHeartbeat();

onErrorCaptured(error => !error.message.includes('Redirected when going from'));

onErrorCaptured(error => !error.message.includes('Redirected when going from'));

const notifications = useNotificationsStore();

async function checkNotifications() {
    const isPageLoaded = route.matched.length > 0;

    try {
        // We have a couple of routes that only serve as entry points to
        // trigger redirects or auth related things but don't render a
        // component themselves. We don't want to check notifications on
        // such routes because it's pointless and it can lead to
        // unnecessary redirects on auth related routes.
        // This is only a workaround. Ideally we don't want to
        // initialize `App.vue` at all for such routes.
        if (isPageLoaded && auth.isLoggedIn) {
            const response = await notificationsService.get();
            notifications.set(response.data.meta);
        }
    } catch (error) {
        // Ignore network errors because notifications are not critical.
        if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.error(error);
        }
    }

    // Make sure to fetch notifications quickly after page load.
    const timeout = isPageLoaded ? TIME_NOTIFICATIONS_USER : 1000;
    setTimeout(checkNotifications, timeout);
}

function enableTracking() {
    const trackerDependencies = [];
    if (config.gtm) {
        const gtm = useGtm();
        trackerDependencies.push(gtm);
    }
    enableTrackers(trackerDependencies);
}

function tryToTrackServerSideEvents() {
    try {
        trackServerSideEvents();
        trackGa4ServerSideEvents();
    } catch (exception) {
        // Temporary log errors for debugging.
        /* istanbul ignore next */
        logger.info({
            exception,
            extras: {
                cookie: getCookie(COOKIE_SERVER_SIDE_EVENTS),
                ga4cookie: getCookie(COOKIE_GA4_SERVER_SIDE_EVENTS),
            },
            tags: {
                user_info: logger.userInfoTags.NONE,
            },
        });
    }
}

onBeforeMount(async () => {
    checkNotifications();

    await router.isReady();
    enableTracking();
    tryToTrackServerSideEvents();
});

const loading = useLoadingStore();
</script>

<template>
    <div id="app">
        <LayoutLoading v-if="loading.initialLoadingOverlay"/>
        <router-view v-else/>
        <AppPopup data-qa="popup"/>
        <AppSnackbar
            v-if="shouldShowSnackbar"
            v-bind="snackbarData"
        />
    </div>
</template>

<style lang="scss">
@import '~@karriereat/global-styles/scss/generic/all';
@import '../assets/scss/elements/base';
@import '~@karriereat/global-styles/scss/objects/all';
@import '~@karriereat/global-styles/scss/components/all';
@import '../assets/scss/hacks/flexbox-min-width-fix';
@import '../assets/scss/utilities/bold';
@import '../assets/scss/utilities/clearfix';
@import '../assets/scss/utilities/ellipsis';
@import '../assets/scss/utilities/nowrap';
@import '../assets/scss/utilities/partial-hover';
@import '../assets/scss/utilities/screen-reader-only';
@import '../assets/scss/utilities/text';
@import '../assets/scss/utilities/word-break';
@import '../assets/scss/settings/border-radius';
@import '../assets/scss/settings/color';
</style>
